import { Link } from "react-router-dom";

import { useMe } from "@/api/hooks";
import { useDashboard } from "@/api/sales";
import { useContracts, useTicketCounts } from "@/api/service";
import { BarList, ColumnChart, StatTile } from "@/components/charts/charts";
import { StatusBadge } from "@/components/crm/Badge";
import { Alert, PageHeader, Spinner } from "@/components/ui";
import { money } from "@/lib/format";
import { can } from "@/lib/permissions";
import { displayPrefs } from "@/lib/time";

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function ServiceRow() {
  const counts = useTicketCounts().data;
  const due = useContracts({ renewal_due: true }).data;
  return (
    <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Service">
      <Link to="/service" className="block"><StatTile label="Open service tickets" value={String(counts?.open ?? "–")} note={`${counts?.mine ?? 0} assigned to you`} /></Link>
      <Link to="/service" className="block"><StatTile label="Overdue tickets" value={String(counts?.overdue ?? "–")} note="Past their fix-by time" tone={counts?.overdue ? "danger" : undefined} /></Link>
      <Link to="/amc" className="block"><StatTile label="AMC renewals due" value={String(due?.length ?? "–")} note="Ending in the next 60 days" tone={due?.length ? "warn" : undefined} /></Link>
    </div>
  );
}

const KIND_LABEL: Record<string, string> = { note: "Notes", call: "Calls", meeting: "Meetings", visit: "Site visits", email: "Emails", whatsapp: "WhatsApp" };

export function DashboardPage() {
  const me = useMe();
  const dash = useDashboard();
  if (dash.isLoading) return <Spinner />;
  if (dash.error) return <Alert>{dash.error.message}</Alert>;
  const d = dash.data!;
  const m = (v: number) => money(v, d.currency);
  const compact = (v: number) => money(v, d.currency, true);
  const firstName = me.data?.user.full_name.split(" ")[0] ?? "";
  const monthLabel = (ym: string) => new Date(`${ym}-01T00:00:00Z`).toLocaleDateString(displayPrefs().locale, { month: "short", timeZone: "UTC" });

  return (
    <>
      <PageHeader title={`Good to see you, ${firstName}`} subtitle="Your pipeline, results and follow-ups at a glance." />
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatTile label="Open pipeline" value={m(d.open_amount)} note={plural(d.open_count, "open deal")} />
        <StatTile label="Weighted forecast" value={m(d.weighted_amount)} note="By stage probability" />
        <StatTile label="Won this month" value={m(d.won_this_month_amount)} note={plural(d.won_this_month_count, "deal")} />
        <StatTile label="Win rate, 90 days" value={d.win_rate_90d === null ? "—" : `${Math.round(d.win_rate_90d * 100)}%`}
          note={d.avg_won_deal_90d === null ? "No closed deals yet" : `Average won ${m(d.avg_won_deal_90d)}`} />
        <StatTile label="Quotes sent, 30 days" value={String(d.quotes_sent_30d)} note={`${d.quotes_accepted_30d} accepted · ${m(d.quote_value_sent_30d)}`} />
        <StatTile label="Overdue tasks" value={String(d.overdue_tasks)} note={`${d.tasks_due_today} due today`} tone={d.overdue_tasks ? "danger" : undefined} />
      </div>
      {can(me.data?.permissions ?? [], "orders:read") && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Link to="/orders" className="block"><StatTile label="Open sales orders" value={m(d.open_orders_value)} note={plural(d.open_orders, "order")} /></Link>
          <Link to="/orders" className="block"><StatTile label="Overdue deliveries" value={String(d.overdue_deliveries)} note="Orders past a delivery date" tone={d.overdue_deliveries ? "danger" : undefined} /></Link>
          <Link to="/quotes?approval=pending" className="block"><StatTile label="Awaiting your approval" value={String(d.approvals_pending)} note="Quotes over a discount limit" tone={d.approvals_pending ? "warn" : undefined} /></Link>
          <StatTile label="Receivables" value={m(d.receivables)} note="Unpaid, from your accounting system" />
        </div>
      )}
      {can(me.data?.permissions ?? [], "service:read") && <ServiceRow />}
      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <BarList title="Pipeline by stage" subtitle="Value of open deals in each stage of the default pipeline" valueHeader="Value"
          points={d.pipeline_by_stage.filter((p) => p.kind === "open").map((p) => ({ key: p.stage, label: p.stage, value: p.amount, display: m(p.amount), detail: plural(p.count, "deal") }))} />
        <ColumnChart title="Won value by month" subtitle="Last six months" valueHeader="Won" ticks={compact}
          points={d.results_by_month.map((p) => ({ key: p.month, label: monthLabel(p.month), value: p.won_amount, display: m(p.won_amount), detail: `${p.won_count} won, ${p.lost_count} lost` }))} />
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <section className="card lg:col-span-2" aria-label="Largest open deals">
          <h2 className="border-b border-line px-5 py-3 font-semibold">Largest open deals</h2>
          {d.top_open_deals.length === 0 ? <p className="px-5 py-6 text-sm text-muted">No open deals yet. <Link to="/deals" className="text-brand hover:underline">Add one</Link>.</p> : (
            <ul className="divide-y divide-line">
              {d.top_open_deals.map((deal) => (
                <li key={deal.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <Link to={`/deals/${deal.id}`} className="min-w-0 hover:text-brand"><span className="block truncate font-medium">{deal.name}</span><span className="text-xs text-muted">{[deal.account?.name, deal.stage.name].filter(Boolean).join(" · ")}</span></Link>
                  <span className="flex shrink-0 items-center gap-3 text-sm font-medium">{money(deal.amount, deal.currency)}<StatusBadge status={deal.status} /></span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="card p-5" aria-label="Activity this week">
          <h2 className="mb-3 font-semibold">Activity this week</h2>
          {d.activities_7d.length === 0 ? <p className="text-sm text-muted">Nothing logged in the last 7 days.</p> : (
            <dl className="space-y-2">
              {d.activities_7d.map((a) => <div key={a.kind} className="flex justify-between text-sm"><dt className="text-muted">{KIND_LABEL[a.kind] ?? a.kind}</dt><dd className="font-medium tabular-nums">{a.count}</dd></div>)}
            </dl>
          )}
          <Link to="/tasks" className="mt-4 inline-block text-sm font-medium text-brand hover:underline">Go to tasks →</Link>
        </section>
      </div>
    </>
  );
}
